import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { Calendar } from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import { simpleDate, toLocalDate } from "@/utils/datetime";
import {
  TAB_IDS,
  TRANSLATION_PREFIX,
  TabId
} from "@/components/global/Chat/components/meetingPrep/consts";
import OverviewTab from "@/components/global/Chat/components/meetingPrep/components/OverviewTab";
import KeyPeopleTab from "@/components/global/Chat/components/meetingPrep/components/KeyPeopleTab";
import RecentNewsTab from "@/components/global/Chat/components/meetingPrep/components/RecentNewsTab";
import { MeetingPrepBrief } from "@/entities/meetingPrep/types";

interface Props {
  brief: MeetingPrepBrief;
  className?: string;
}

const MeetingPrepCard: React.FC<Props> = ({ brief, className }) => {
  const { t } = useTranslation();
  const { overview, meetingDate, meetingTitle } = brief;
  const keyPeople = brief.keyPeople ?? [];
  const recentNews = brief.recentNews ?? [];

  // Every tab is always offered; an empty section shows its own empty state.
  const [activeTab, setActiveTab] = useState<TabId>("overview");

  const panels: Record<TabId, React.ReactNode> = {
    overview: <OverviewTab brief={brief} />,
    keyPeople: <KeyPeopleTab keyPeople={keyPeople} />,
    recentNews: <RecentNewsTab recentNews={recentNews} />
  };

  return (
    <div
      className={cn(
        "flex flex-col w-full max-h-[520px] overflow-hidden rounded-2xl border-[0.5px] border-border-border1 bg-surface-elevation2 shadow-[0px_4px_12px_-8px_rgba(12,17,29,0.24)]",
        className
      )}
      data-test="meeting-prep-card"
    >
      <div className="flex items-center gap-3 p-3">
        <div className="flex items-center justify-center shrink-0 size-8 rounded-[10px] border-[0.8px] border-white-8 bg-white-4">
          <IconContainer size="5" color="secondary">
            <Calendar />
          </IconContainer>
        </div>
        <div className="flex flex-col flex-1 min-w-0">
          <Text
            as="p"
            variant="lgBody"
            hasTruncatedTooltip
            className="min-w-0 truncate"
          >
            {t(`${TRANSLATION_PREFIX}.cardTitle`, {
              company: overview.companyName,
              date: meetingDate
                ? simpleDate(toLocalDate(meetingDate), "long")
                : ""
            })}
          </Text>
          {meetingTitle ? (
            <Text
              as="p"
              variant="bodySecondary"
              hasTruncatedTooltip
              className="min-w-0 truncate"
              data-test="meeting-prep-meeting-title"
            >
              {meetingTitle}
            </Text>
          ) : null}
        </div>
      </div>

      <div className="flex items-end gap-1 border-b border-border-border1 px-2">
        {TAB_IDS.map((tab) => {
          const isSelected = tab === activeTab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className="flex flex-col items-center gap-[7px] pt-2"
              data-test={`meeting-prep-tab-${tab}`}
            >
              <span className="px-1.5 py-1">
                <Text
                  as="span"
                  variant={isSelected ? "label" : "bodySecondary"}
                >
                  {t(`${TRANSLATION_PREFIX}.tabs.${tab}`)}
                </Text>
              </span>
              <span
                className={cn(
                  "h-[2px] w-full mb-[-1px]",
                  isSelected ? "bg-cta" : "bg-transparent"
                )}
              />
            </button>
          );
        })}
      </div>

      {/* Stacked panels keep the card at the tallest tab's height; each scrolls on its own so a hidden tall tab can't make a short one scroll. */}
      <div className="grid flex-1 min-h-0 grid-rows-[minmax(0,1fr)]">
        {TAB_IDS.map((tab) => (
          <div
            key={tab}
            className={cn(
              "col-start-1 row-start-1 min-h-0 overflow-y-auto",
              tab !== activeTab && "invisible"
            )}
            data-test={`meeting-prep-panel-${tab}`}
          >
            {panels[tab]}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MeetingPrepCard;
